// Patient passport-photo pipeline (client side).
// Contract: final image is EXACTLY 413×531 px (35×45 mm at 300 DPI) JPEG with
// a 300-DPI JFIF APP0 header — validated server-side (apps/api/src/lib/jpeg.ts).

export const PASSPORT_W = 413;
export const PASSPORT_H = 531;
export const PASSPORT_DPI = 300;

/**
 * Patch (or insert) the JFIF APP0 segment so the JPEG carries 300 DPI.
 * Canvas toBlob('image/jpeg') writes density 1×1 / aspect-only — every viewer
 * would report 72 DPI. We rewrite the APP0 segment bytes directly:
 *   FF E0 | len=0x0010 | "JFIF\0" | ver 1.2 | units=1 (dpi) | X=300 | Y=300
 * If an APP0 exists right after SOI (all major browsers emit one for canvas
 * JPEGs), its density fields are patched in place; otherwise a fresh 18-byte
 * APP0 is spliced in after the SOI marker.
 */
export function patchJfif300Dpi(jpeg: ArrayBuffer): ArrayBuffer {
  const src = new Uint8Array(jpeg);
  const JFIF = [0x4a, 0x46, 0x49, 0x46, 0x00]; // "JFIF\0"
  const dpiHi = (PASSPORT_DPI >> 8) & 0xff;
  const dpiLo = PASSPORT_DPI & 0xff;

  if (src.length >= 18 && src[2] === 0xff && src[3] === 0xe0) {
    // APP0 present — verify it is a JFIF segment before patching.
    const isJfif = JFIF.every((b, i) => src[6 + i] === b);
    if (isJfif) {
      const out = src.slice();
      out[13] = 1; // units = dots per inch
      out[14] = dpiHi;
      out[15] = dpiLo;
      out[16] = dpiHi;
      out[17] = dpiLo;
      return out.buffer;
    }
  }

  // No JFIF APP0 at the expected spot — splice one in after SOI (FF D8).
  const app0 = new Uint8Array([
    0xff, 0xe0, 0x00, 0x10, ...JFIF, 0x01, 0x02, 0x00, 1, dpiHi, dpiLo, dpiHi, dpiLo, 0x00, 0x00,
  ]);
  const out = new Uint8Array(2 + app0.length + (src.length - 2));
  out[0] = src[0];
  out[1] = src[1];
  out.set(app0, 2);
  out.set(src.subarray(2), 2 + app0.length);
  return out.buffer;
}

/** Blob → base64 (no data-URL prefix) for JSON transport to the API. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export interface CropRect {
  sx: number; // source-x (image px)
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Render the crop region of a decoded image onto a 413×531 canvas and export
 * a 300-DPI JPEG. `crop` must lie inside the image bounds (the crop UI clamps).
 */
export async function cropToPassportJpeg(bitmap: ImageBitmap, crop: CropRect, mirror = false): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = PASSPORT_W;
  canvas.height = PASSPORT_H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (mirror) {
    ctx.translate(PASSPORT_W, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(bitmap, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, PASSPORT_W, PASSPORT_H);
  const raw = await new Promise<ArrayBuffer>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? b.arrayBuffer().then(resolve, reject) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      0.92,
    );
  });
  return new Blob([patchJfif300Dpi(raw)], { type: "image/jpeg" });
}

/** Decode a captured frame / chosen file into an ImageBitmap (EXIF auto-applied). */
export async function decodeImage(source: Blob): Promise<ImageBitmap> {
  return createImageBitmap(source);
}
